import React, { useState, useEffect } from "react";
import { Table, Form as BootstrapForm, Row, Col, Badge, InputGroup, Button } from "react-bootstrap";
import { Formik, Form, Field, FieldArray } from "formik";
import { BiPlus, BiSave, BiTrash, BiGridAlt, BiCheckDouble, BiWrench } from "react-icons/bi";
import { FaHashtag, FaBatteryFull } from "react-icons/fa";
import BrandModal from "../core/components/Brand/BrandModal";

const UnitRow = React.memo(({ idx, unit, isDevice, policies, setFieldValue, handleKeyDown, generateSerial, remove, isDuplicate }) => {
  const CONDITION_LIST = ["Brand New", "Used Grade A", "Used Grade B", "Refurbished", "Damaged", "Used", "Other"];
  const POWER_STATUS_TAGS = [
    "Battery Replaced (A++)", "Battery Changed (Genuine)", "Battery Health Boosted", "Battery Cycle Reset", "Power IC Repaired"
  ];
  const COMMON_REFURB_TAGS = [
    ...POWER_STATUS_TAGS,
    "Display Replaced (Genuine)", "Display Changed (High-Copy)", "OLED Premium", "TrueTone Restored", "Touch Glass Replaced",
    "FaceID Verified", "TouchID Verified", "IC Power Repaired",
    "Camera Replaced", "Lens Changed", "Speaker Cleaned",
    "Case Changed", "Back Glass Replaced", "Water Seal Intact",
    "3uTools Green", "Factory Unlocked", "Region: LL/A (USA)"
  ];

  const isMissing = !unit.serialNumber?.trim();
  const rowStyle = isMissing
    ? { backgroundColor: '#fff5f5' }
    : isDuplicate
      ? { backgroundColor: '#fffcf0' }
      : {};

  return (
    <tr style={{ ...rowStyle }}>
      <td className="text-center position-relative" style={{ width: '45px' }}>
        <div className="fw-black small text-dark mt-1">{idx + 1}</div>
        {isMissing ? (
          <>
            <div style={{ position: 'absolute', top: 0, left: 0, width: '8px', height: '100%', backgroundColor: '#dc3545', boxShadow: '2px 0 5px rgba(220, 53, 69, 0.2)' }} />
            <BiPlus className="text-danger position-absolute" style={{ top: 2, right: 2, fontSize: '0.7rem' }} />
          </>
        ) : isDuplicate ? (
          <>
            <div style={{ position: 'absolute', top: 0, left: 0, width: '8px', height: '100%', backgroundColor: '#ffc107', boxShadow: '2px 0 5px rgba(255, 193, 7, 0.2)' }} />
            <BiGridAlt className="text-warning position-absolute" style={{ top: 2, right: 2, fontSize: '0.7rem' }} />
          </>
        ) : null}
      </td>
      <td className="ps-3">
        <div className="d-flex gap-1" style={{ maxWidth: 280 }}>
          <div className="position-relative flex-grow-1">
            <Field
              name={`units[${idx}].serialNumber`}
              className={`form-control form-control-sm serial-input ${isMissing ? 'border-danger border-2' : ''} ${isDuplicate ? 'border-warning border-2' : ''}`}
              placeholder="SCAN SERIAL..."
              id={`modal-serial-${idx}`}
              onKeyDown={(e) => handleKeyDown(e, idx)}
              autoComplete="off"
              spellCheck="false"
            />
            {isDuplicate && !isMissing && (
              <Badge bg="warning" text="dark" className="position-absolute shadow-sm" style={{ top: -10, right: 5, fontSize: '0.6rem', fontWeight: 900, border: '1px solid #000' }}>DUPLICATE !</Badge>
            )}
            {isMissing && (
              <Badge bg="danger" className="position-absolute shadow-sm" style={{ top: -2, right: 5, fontSize: '0.6rem', fontWeight: 900 }}>REQUIRED</Badge>
            )}
          </div>
          <Button
            variant="light"
            size="sm"
            onClick={() => generateSerial(idx)}
            className="px-2 x-small border fw-bold text-dark h-100"
          >
            GEN
          </Button>
        </div>
      </td>
      <td>
        <Field as="select" name={`units[${idx}].condition`} className="form-select form-select-sm fw-bold">
          {CONDITION_LIST.map(c => <option key={c} value={c}>{c}</option>)}
        </Field>
      </td>
      {isDevice && (
        <td>
          <InputGroup size="sm">
            <Field name={`units[${idx}].batteryHealth`} type="number" className="form-control text-center fw-bold text-dark" />
            <InputGroup.Text className="p-1"><FaBatteryFull className="text-success" size={12} /></InputGroup.Text>
          </InputGroup>
        </td>
      )}
      <td>
        {unit.condition !== "Brand New" ? (
          <div className="d-flex flex-wrap gap-1 border rounded p-1 bg-white" style={{ minHeight: '34px' }}>
            {COMMON_REFURB_TAGS.map(tag => (
              <Badge
                key={tag}
                pill
                bg={unit.refurb_tags?.includes(tag) ? "primary" : "light"}
                text={unit.refurb_tags?.includes(tag) ? "white" : "dark"}
                className="cursor-pointer x-small border"
                style={{ fontSize: '0.65rem' }}
                onClick={() => {
                  const currentTags = unit.refurb_tags || [];
                  const nextTags = currentTags.includes(tag)
                    ? currentTags.filter(t => t !== tag)
                    : [...currentTags, tag];
                  setFieldValue(`units[${idx}].refurb_tags`, nextTags);
                }}
              >
                {tag}
              </Badge>
            ))}
          </div>
        ) : (
          <div className="text-center text-muted x-small pt-2 fw-bold">ORIGINAL / SEALED</div>
        )}
      </td>
      <td>
        <Field as="select" name={`units[${idx}].warrantyPolicyId`} className="form-select form-select-sm x-small fw-bold">
          <option value={null}>-- No Warranty --</option>
          {policies.map(p => (
            <option key={p._id} value={p._id}>{p.name}</option>
          ))}
        </Field>
      </td>
      <td><Field name={`units[${idx}].unitCost`} type="number" className="form-control form-control-sm text-end fw-bold" /></td>
      <td><Field name={`units[${idx}].sellingPrice`} type="number" className="form-control form-control-sm text-end fw-bold text-primary" /></td>
      <td className="text-center">
        <BiTrash className="text-danger cursor-pointer" onClick={() => remove(idx)} size={20} />
      </td>
    </tr>
  );
});

const VariantAndUnitsModal = ({ show, onHide, onSave, baseItem, existingData, mode = 'DIRECT', policies = [] }) => {
  const BRAND_COLOR = "#001c3a";
  const [bulkCost, setBulkCost] = useState(existingData?.unitCost || baseItem?.lastUnitCost || 0);
  const [bulkPrice, setBulkPrice] = useState(existingData?.sellingPrice || baseItem?.defaultSellingPrice || 0);
  const [bulkWarranty, setBulkWarranty] = useState("");
  const [bulkCondition, setBulkCondition] = useState("Brand New");
  const [bulkBattery, setBulkBattery] = useState(100);
  const [serialPrefix, setSerialPrefix] = useState("");
  const [serialStart, setSerialStart] = useState("");
  const [quickAddQty, setQuickAddQty] = useState(1);
  const isDevice = baseItem?.category === "Device";

  useEffect(() => {
    if (show && baseItem) {
      setBulkCost(existingData?.unitCost || baseItem.lastUnitCost || 0);
      setBulkPrice(existingData?.sellingPrice || baseItem.defaultSellingPrice || 0);
      setBulkWarranty(existingData?.warrantyPolicyId || baseItem.warrantyPolicyId || "");
    }
  }, [baseItem?._id, show]);

  const handleFormSubmit = (values) => {
    const formattedUnits = values.units.map(u => ({
      ...u,
      batteryHealth: isDevice ? (u.condition === "Brand New" ? 100 : (Number(u.batteryHealth) || 0)) : null,
      unitCost: Number(u.unitCost) || 0,
      sellingPrice: Number(u.sellingPrice) || 0,
      refurb_tags: u.condition === "Brand New" ? [] : (u.refurb_tags || [])
    }));

    const purchaseQty = formattedUnits.length;
    const total_price = formattedUnits.reduce((sum, u) => sum + (parseFloat(u.unitCost) || 0), 0);
    const unitCost = formattedUnits.length > 0 ? parseFloat(formattedUnits[0].unitCost) || 0 : 0;
    const sellingPrice = formattedUnits.length > 0 ? parseFloat(formattedUnits[0].sellingPrice) || 0 : 0;

    const baseName = existingData?.variantName || baseItem?.itemName || "Unknown Item";
    const attrString = values.variantAttributes?.length > 0
      ? ` (${values.variantAttributes.map(a => a.value).filter(Boolean).join(' / ')})`
      : '';
    const descriptiveName = `${baseName}${attrString}`;

    const payload = {
      ...existingData,
      item_id: baseItem._id,
      variant_id: baseItem.variant_id || existingData?.variant_id || null,
      variantAttributes: values.variantAttributes,
      itemName: descriptiveName,
      serializedItems: formattedUnits,
      purchaseQty,
      unitCost,
      sellingPrice,
      total_price,
      mode
    };
    onSave(payload);
  };

  const handleKeyDown = React.useCallback((e, idx) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const nextInput = document.getElementById(`modal-serial-${idx + 1}`);
      if (nextInput) {
        nextInput.focus();
      }
    }
  }, []);

  const modalTitle = existingData?.variantAttributes && existingData.variantAttributes.length > 0
    ? `${existingData.variantName || baseItem?.itemName} - ${existingData.variantBarcode || baseItem?.barcode}`
    : `${baseItem?.itemName} - ${baseItem?.barcode}`;

  const memoizedInitialValues = React.useMemo(() => ({
    variantAttributes: existingData?.variantAttributes || [{ key: "Color", value: "" }],
    units: (existingData?.serializedItems?.length > 0)
      ? existingData.serializedItems.map((u, idx) => ({ ...u, tempId: u.tempId || idx }))
      : [{
        tempId: 'initial-item',
        serialNumber: "",
        unitCost: existingData?.unitCost || baseItem?.lastUnitCost || 0,
        sellingPrice: existingData?.sellingPrice || baseItem?.defaultSellingPrice || 0,
        condition: "Brand New",
        batteryHealth: 100,
        refurb_tags: [],
        warrantyPolicyId: existingData?.warrantyPolicyId || baseItem?.warrantyPolicyId || null
      }]
  }), [existingData?.serializedItems, existingData?._id, existingData?.tempId, baseItem?._id]);

  const CONDITION_LIST = ["Brand New", "Used Grade A", "Used Grade B", "Refurbished", "Damaged", "Used", "Other"];

  return (
    <BrandModal show={show} onHide={onHide} size="fullscreen" title={modalTitle} icon={<BiGridAlt />} footer={null}>
      <Formik
        key={existingData?.tempId || baseItem?._id}
        initialValues={memoizedInitialValues}
        enableReinitialize={true}
        onSubmit={handleFormSubmit}
      >
        {({ values, setFieldValue, submitForm }) => {

          const generateSerial = (idx) => {
            const prefix = (baseItem?.itemName || "ITM").substring(0, 3).toUpperCase();
            const timestamp = Date.now().toString().slice(-6);
            const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
            setFieldValue(`units[${idx}].serialNumber`, `${prefix}${timestamp}${random}`);
          };

          const handleApplyAll = () => {
            const cost = Number(bulkCost) || 0;
            const price = Number(bulkPrice) || 0;
            const bat = Number(bulkBattery) || 0;

            const updated = values.units.map(u => ({
              ...u,
              unitCost: cost,
              sellingPrice: price,
              condition: bulkCondition,
              batteryHealth: isDevice ? bat : null,
              warrantyPolicyId: bulkWarranty || u.warrantyPolicyId
            }));
            setFieldValue("units", updated);
          };

          const handleBulkGenerateSerials = () => {
            if (!serialStart) return;
            const startNum = parseInt(serialStart);
            const updated = values.units.map((u, i) => {
              const currentNum = startNum + i;
              const formattedNum = currentNum.toString().padStart(serialStart.length, '0');
              return { ...u, serialNumber: `${serialPrefix}${formattedNum}` };
            });
            setFieldValue("units", updated);
          };

          const handleBulkGenerateRandomSerials = () => {
            const prefix = (baseItem?.itemName || "ITM").substring(0, 3).toUpperCase();
            const updated = values.units.map((u, i) => {
              const timestamp = (Date.now() + i).toString().slice(-7);
              const rand = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
              return { ...u, serialNumber: `${prefix}${timestamp}${rand}` };
            });
            setFieldValue("units", updated);
          };

          const duplicateSerials = new Set();
          const seenSerials = new Set();
          values.units.forEach(u => {
            const sn = u.serialNumber?.trim();
            if (sn) {
              if (seenSerials.has(sn)) {
                duplicateSerials.add(sn);
              }
              seenSerials.add(sn);
            }
          });

          return (
            <Form style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <div className="border-bottom bg-white shadow-sm" style={{ flexShrink: 0, padding: '10px 16px' }}>
                <div className="d-flex align-items-center gap-3 flex-wrap">
                  <div className="d-flex align-items-center gap-2 me-2" style={{ borderRight: '2px solid #eee', paddingRight: '16px' }}>
                    <div style={{ background: '#001c3a', color: '#fff', borderRadius: 6, padding: '4px 18px', fontWeight: 900, fontSize: '1.8rem', lineHeight: 1.1, minWidth: 60, textAlign: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                      {values.units.length}
                    </div>
                    <div>
                      <div style={{ fontWeight: 900, fontSize: '0.75rem', color: '#333', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Units</div>
                      <div className="d-flex gap-2 align-items-center mt-1">
                        <Badge bg="success" style={{ fontSize: '0.65rem', padding: '3px 6px' }}>{values.units.filter(u => u.serialNumber?.trim()).length} OK</Badge>
                        {values.units.some(u => !u.serialNumber?.trim()) && (
                          <Badge bg="danger" style={{ fontSize: '0.65rem', padding: '3px 6px', boxShadow: '0 0 5px rgba(220,53,69,0.3)' }}>
                            {values.units.filter(u => !u.serialNumber?.trim()).length} MISSING
                          </Badge>
                        )}
                        {duplicateSerials.size > 0 && (
                          <Badge bg="warning" text="dark" style={{ fontSize: '0.65rem', padding: '3px 6px', border: '1px solid #000', boxShadow: '0 0 5px rgba(255,193,7,0.3)' }}>
                            {duplicateSerials.size} DUPLICATE
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>

                  <div style={{ width: 105 }}>
                    <label style={{ fontSize: '0.6rem', fontWeight: 900, color: '#000', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 2 }}>Add Rows</label>
                    <InputGroup size="sm">
                      <BootstrapForm.Control type="number" min="1" className="toolbar-field text-center" value={quickAddQty} onChange={e => setQuickAddQty(parseInt(e.target.value) || 1)} />
                      <Button variant="success" className="fw-bold px-2" style={{ height: 30 }} onClick={() => {
                        const now = Date.now();
                        const newItems = Array.from({ length: quickAddQty }, (_, i) => ({
                          tempId: now + i,
                          serialNumber: "",
                          unitCost: Number(bulkCost) || 0,
                          sellingPrice: Number(bulkPrice) || 0,
                          condition: bulkCondition,
                          batteryHealth: isDevice ? bulkBattery : 100,
                          refurb_tags: [],
                          warrantyPolicyId: bulkWarranty || null
                        }));
                        setFieldValue("units", [...values.units, ...newItems]);
                      }}><BiPlus /></Button>
                    </InputGroup>
                  </div>
                  <div style={{ width: 1, height: 36, background: '#ddd' }} />

                  {[
                    { label: 'Cost', width: 85, el: <BootstrapForm.Control size="sm" type="number" className="toolbar-field" value={bulkCost} onChange={e => setBulkCost(e.target.value)} /> },
                    { label: 'Price', width: 85, el: <BootstrapForm.Control size="sm" type="number" className="toolbar-field" value={bulkPrice} onChange={e => setBulkPrice(e.target.value)} /> },
                    { label: 'Condition', width: 125, el: <BootstrapForm.Select size="sm" className="toolbar-field" value={bulkCondition} onChange={e => setBulkCondition(e.target.value)}>{CONDITION_LIST.map(c => <option key={c} value={c}>{c}</option>)}</BootstrapForm.Select> },
                    ...(isDevice ? [{ label: 'Bat %', width: 65, el: <BootstrapForm.Control size="sm" type="number" className="toolbar-field" value={bulkBattery} onChange={e => setBulkBattery(e.target.value)} /> }] : []),
                    { label: 'Warranty', width: 135, el: <BootstrapForm.Select size="sm" className="toolbar-field" value={bulkWarranty} onChange={e => setBulkWarranty(e.target.value)}><option value="">None</option>{policies.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}</BootstrapForm.Select> },
                  ].map(({ label, width, el }) => (
                    <div key={label} style={{ width }}>
                      <label style={{ fontSize: '0.6rem', fontWeight: 900, color: '#000', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 2 }}>{label}</label>
                      {el}
                    </div>
                  ))}

                  <div style={{ width: 1, height: 36, background: '#ddd' }} />

                  <div className="d-flex gap-1 align-items-end">
                    <div style={{ width: 70 }}>
                      <label style={{ fontSize: '0.6rem', fontWeight: 900, color: '#555', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 2 }}>Prefix</label>
                      <BootstrapForm.Control size="sm" placeholder="AP-" className="toolbar-field" value={serialPrefix} onChange={e => setSerialPrefix(e.target.value)} />
                    </div>
                    <div style={{ width: 60 }}>
                      <label style={{ fontSize: '0.6rem', fontWeight: 900, color: '#555', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 2 }}>Start</label>
                      <BootstrapForm.Control size="sm" placeholder="001" className="toolbar-field" value={serialStart} onChange={e => setSerialStart(e.target.value)} />
                    </div>
                    <Button variant="outline-dark" size="sm" className="fw-bold px-2" style={{ height: 30, fontSize: '0.68rem' }} onClick={handleBulkGenerateSerials}>
                      <BiWrench size={11} className="me-1" />SEQ
                    </Button>
                    <Button variant="outline-primary" size="sm" className="fw-bold px-2" style={{ height: 30, fontSize: '0.68rem' }} onClick={handleBulkGenerateRandomSerials}>
                      <BiGridAlt size={11} className="me-1" />RND
                    </Button>
                  </div>




                  <div style={{ flex: 1 }} />

                  <div className="d-flex gap-2">
                    <Button variant="outline-danger" size="sm" className="fw-bold px-3" style={{ height: 30, fontSize: '0.72rem' }}
                      onClick={() => { if (window.confirm('Delete ALL rows?')) setFieldValue('units', []) }}>
                      <BiTrash size={13} className="me-1" />CLEAR ALL
                    </Button>
                    <Button variant="dark" size="sm" className="fw-bold px-3" style={{ height: 30, fontSize: '0.72rem', backgroundColor: BRAND_COLOR }}
                      onClick={handleApplyAll}>
                      <BiCheckDouble size={13} className="me-1" />APPLY ALL
                    </Button>
                  </div>
                </div>
              </div>

              <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                <Table hover size="sm" className="mb-0 align-middle">
                  <thead className="bg-dark text-white sticky-top" style={{ marginBottom: 100, top: 0, zIndex: 10 }}>
                    <tr className="x-small text-uppercase fw-black">
                      <th className="text-center text-white-50" style={{ width: '45px' }}>#</th>
                      <th className="ps-3" style={{ width: '20%' }}>IMEI / Serial</th>
                      <th style={{ width: '12%' }}>Condition</th>
                      {isDevice && <th style={{ width: '8%' }}>Bat %</th>}
                      <th style={{ width: '22%' }}>Refurb Tags</th>
                      <th style={{ width: '15%' }}>Warranty</th>
                      <th className="text-end" style={{ width: '10%' }}>Cost</th>
                      <th className="text-end" style={{ width: '10%' }}>Sale Price</th>
                      <th style={{ width: '40px' }}></th>
                    </tr>
                  </thead>
                  <tbody >
                    <FieldArray name="units">
                      {({ push, remove }) => (
                        <>
                          {values.units.map((unit, idx) => (
                            <UnitRow
                              key={unit.tempId || idx}
                              idx={idx}
                              unit={unit}
                              isDevice={isDevice}
                              policies={policies}
                              setFieldValue={setFieldValue}
                              handleKeyDown={handleKeyDown}
                              generateSerial={generateSerial}
                              remove={remove}
                              isDuplicate={duplicateSerials.has(unit.serialNumber?.trim())}
                            />
                          ))}
                          <tr className="bg-light cursor-pointer" onClick={() => push({
                            tempId: Date.now() + Math.random(),
                            serialNumber: "",
                            unitCost: Number(bulkCost) || 0,
                            sellingPrice: Number(bulkPrice) || 0,
                            condition: bulkCondition,
                            batteryHealth: isDevice ? bulkBattery : 100,
                            refurb_tags: [],
                            warrantyPolicyId: bulkWarranty || null
                          })}>
                            <td colSpan="100%" className="text-center py-3 text-primary small fw-bold"><BiPlus /> INSERT NEW ASSET ROW</td>
                          </tr>
                        </>
                      )}
                    </FieldArray>
                  </tbody>
                </Table>
              </div>

              <div className="px-4 py-3 border-top bg-white d-flex justify-content-between align-items-center" style={{ flexShrink: 0 }}>
                <div className="d-flex align-items-center gap-4">
                  <div className="d-flex gap-4">
                    <div>
                      <div style={{ fontSize: '0.6rem', fontWeight: 900, color: '#888', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Serials</div>
                      <div className="d-flex align-items-baseline gap-2">
                        <span style={{ fontSize: '0.85rem', fontWeight: 900, color: '#000' }}>{values.units.filter(u => u.serialNumber?.trim()).length} / {values.units.length}</span>
                        {values.units.some(u => !u.serialNumber?.trim()) && (
                          <Badge bg="danger" className="x-small" style={{ fontSize: '0.6rem' }}>{values.units.filter(u => !u.serialNumber?.trim()).length} MISSING</Badge>
                        )}
                        {duplicateSerials.size > 0 && (
                          <Badge bg="warning" text="dark" className="x-small" style={{ fontSize: '0.6rem' }}>{duplicateSerials.size} DUPLICATE</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <div style={{ width: 1, height: 32, background: '#eee' }} />
                  <div>
                    <div style={{ fontSize: '0.6rem', fontWeight: 900, color: '#888', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Total Cost</div>
                    <div style={{ fontSize: '0.95rem', fontWeight: 900, color: '#001c3a' }}>
                      {values.units.reduce((sum, u) => sum + (Number(u.unitCost) || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>
                  <div style={{ width: 1, height: 32, background: '#eee' }} />
                  <div>
                    <div style={{ fontSize: '0.6rem', fontWeight: 900, color: '#888', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Total Value</div>
                    <div style={{ fontSize: '0.95rem', fontWeight: 900, color: '#1565c0' }}>
                      {values.units.reduce((sum, u) => sum + (Number(u.sellingPrice) || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>
                </div>
                <div className="d-flex gap-3">
                  <Button variant="outline-secondary" className="px-4 fw-bold" onClick={onHide}>DISCARD</Button>
                  <Button variant="dark" onClick={submitForm} className="px-5 fw-bold" style={{ backgroundColor: BRAND_COLOR }}>
                    <BiSave className="me-2" /> FINISH & SYNC
                  </Button>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik >

      <style>{`
        .x-small { font-size: 0.65rem; }
        .fw-black { font-weight: 900; }

        /* ALL INPUTS: pitch black text, bold */
        .form-control, .form-control-sm { 
          font-size: 0.8rem !important; 
          color: #000000 !important; 
          font-weight: 700 !important;
          background-color: #ffffff !important;
        }
        .form-select, .form-select-sm { 
          font-size: 0.75rem !important; 
          color: #000000 !important; 
          font-weight: 700 !important;
        }
        .font-monospace { 
          font-size: 0.8rem !important; 
          font-weight: 800 !important; 
          color: #000000 !important; 
          letter-spacing: 0.04em;
        }

        /* TABLE ROWS: High contrast zebra for fast scanning */
        .table tbody tr:nth-child(even) { background-color: #f5f5f5 !important; }
        .table-hover tbody tr:hover { background-color: #e8f0fe !important; }
        .table tbody td { color: #000000 !important; font-weight: 600; }

        /* HEADER: deep black */
        thead th { 
          background-color: #0a0a0a !important; 
          color: #ffffff !important; 
          font-weight: 900 !important;
          font-size: 0.7rem !important;
          letter-spacing: 0.06em;
          padding: 10px 8px !important;
          border: none !important;
        }

        /* TOOLBAR LABELS */
        .bulk-toolbar label { color: #000 !important; font-weight: 900 !important; }

        /* SERIAL INPUT: high-contrast fast-entry style */
        .serial-input {
          font-family: 'Courier New', Courier, monospace !important;
          font-size: 0.95rem !important;
          font-weight: 900 !important;
          color: #000000 !important;
          background-color: #fffde7 !important;
          border: 2px solid #bdbdbd !important;
          border-left: 5px solid #1565c0 !important;
          border-radius: 4px !important;
          letter-spacing: 0.08em !important;
          padding: 5px 8px !important;
          height: 36px !important;
          text-transform: uppercase !important;
        }
        .serial-input:focus {
          background-color: #ffffff !important;
          border-color: #1976d2 !important;
          border-left-color: #1976d2 !important;
          border-left-width: 4px !important;
          box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.15) !important;
          outline: none !important;
        }
        .serial-input::placeholder {
          color: #9e9e9e !important;
          font-weight: 500 !important;
          font-size: 0.72rem !important;
        }

        /* TOOLBAR FIELDS: unified look */
        .toolbar-field {
          height: 30px !important;
          font-size: 0.75rem !important;
          font-weight: 700 !important;
          color: #000000 !important;
          border: 1.5px solid #888 !important;
          border-radius: 4px !important;
          padding: 2px 6px !important;
        }
        .toolbar-field:focus {
          border-color: #001c3a !important;
          box-shadow: 0 0 0 2px rgba(0,28,58,0.12) !important;
        }
      `}</style>
    </BrandModal >
  );
};

export default VariantAndUnitsModal;